import {
	Alert,
	Box,
	Button,
	Icons,
	Inline,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import { useEffect, useState } from "react"
import { generatePath, useLocation, useNavigate } from "../router-utils"
import { useAuthUser, useHasFeatureFlag } from "../Auth"
import {
	askNotificationPermission,
	doesBrowserSupportPushNotifications,
	getDeviceNotificationPermissionsStatus,
	isUserDeviceSubscribedForPushNotifications,
	isPushRegistrationSuppressed,
	subscribeToPushSubscriptionChanges,
	whenPushOpsIdle,
} from "./PushNotifications"
import { ButtonLink } from "@sembark-travel/ui-router"

const PREFERENCES_PATH = "/notifications/preferences"
// The preferences page auto-subscribes (and sends a test) when it sees this
// query — that's the only way the alert triggers subscription.
const SUBSCRIBE_SEARCH = "?subscribe=1"
const ENABLE_SNOOZE_KEY = "enable_notifications_nudge_snoozed_until"
const SNOOZE_DURATION = 7 * 24 * 60 * 60 * 1000 // a week

function isSnoozed(key: string): boolean {
	try {
		return Date.now() < Number(localStorage.getItem(key) || 0)
	} catch {
		return false
	}
}

function snooze(key: string) {
	try {
		localStorage.setItem(key, String(Date.now() + SNOOZE_DURATION))
	} catch {
		// ignore storage failures (private mode etc.)
	}
}

/**
 * App-wide banner that nudges the user toward the notification preferences page:
 *
 *  1. Permission still "default" → ask for browser permission (snoozable) and
 *     send them to the preferences page.
 *  2. Permission "granted" but this device is not registered → prompt to finish
 *     setup on the preferences page (hidden while already on that page).
 *
 * Never shown once permission is granted and the device is registered, when the
 * user has blocked notifications, or when push is not configured on the server.
 */
export function EnableNotificationsNudge() {
	const { user } = useAuthUser()
	const navigate = useNavigate()
	const location = useLocation()
	// Only nudge when the server actually has push (Beams) configured.
	const hasPushNotifications = useHasFeatureFlag("push_notifications")
	const supported = doesBrowserSupportPushNotifications()
	const [status, setStatus] = useState<"pending" | NotificationPermission>(() =>
		getDeviceNotificationPermissionsStatus()
	)
	const [registered, setRegistered] = useState<undefined | boolean>(undefined)
	const [dismissed, setDismissed] = useState(false)

	const userId = user?.uid
	const canUsePush = Boolean(user && hasPushNotifications && supported)

	// Once permission is granted, find out whether this device is registered so
	// we know whether to prompt to finish setup. The login sync re-subscribes the
	// device (logout keeps the last-subscriber marker), so a naive one-shot read
	// can catch the transient mid-sync not-registered window and wrongly nudge
	// until a reload. Wait for pending push ops to settle before the first read,
	// and re-read whenever the subscription state changes.
	useEffect(() => {
		if (!canUsePush || !userId || status !== "granted") return
		let active = true
		const check = () =>
			isUserDeviceSubscribedForPushNotifications({ uid: userId })
				.then((yes) => active && setRegistered(yes))
				.catch(() => active && setRegistered(false))
		whenPushOpsIdle().then(() => active && check())
		const unsubscribe = subscribeToPushSubscriptionChanges(check)
		return () => {
			active = false
			unsubscribe()
		}
	}, [canUsePush, userId, status])

	if (
		!canUsePush ||
		!user ||
		dismissed ||
		// The preferences page carries its own setup UI — never nudge there.
		location.pathname === PREFERENCES_PATH
	) {
		return null
	}

	// Mode 1: permission not decided yet → ask for it, then send them to the
	// preferences page to finish.
	if (
		(status === "default" || status === "pending") &&
		!isSnoozed(ENABLE_SNOOZE_KEY)
	) {
		return (
			<Alert status="info" rounded="none" hideIcon>
				<Inline
					gap="4"
					alignItems="center"
					justifyContent="between"
					collapseBelow="sm"
					maxWidth="3xl"
					marginX="auto"
				>
					<Inline gap="3" alignItems="center">
						<Box>
							<Icons.BellAlertSolid size="6" />
						</Box>
						<Stack gap="1">
							<Text fontWeight="semibold">Turn on desktop notifications</Text>
							<Text fontSize="sm" color="muted">
								Get notified of new leads, conversions, messages and followups
								immediately, even when this tab is closed or in the background.
							</Text>
						</Stack>
					</Inline>
					<Inline gap="2">
						<Button
							level="primary"
							size="sm"
							onClick={() => {
								setStatus("pending")
								// Wait for the permission prompt to resolve before routing,
								// otherwise the preferences page re-shows the Enable prompt.
								askNotificationPermission((nextStatus) => {
									setStatus(nextStatus)
									if (nextStatus === "granted") {
										navigate({
											pathname: PREFERENCES_PATH,
											search: SUBSCRIBE_SEARCH,
										})
									}
								})
							}}
						>
							Enable
						</Button>
						<Button
							size="sm"
							onClick={() => {
								snooze(ENABLE_SNOOZE_KEY)
								setDismissed(true)
							}}
						>
							Not now
						</Button>
					</Inline>
				</Inline>
			</Alert>
		)
	}

	// Mode 2: permission granted but this device is not registered → send them
	// to the preferences page to register. Suppressed once a registration has
	// already failed on this device, so we stop nagging (the manual Register
	// button on the preferences page remains available to retry).
	if (
		status === "granted" &&
		registered === false &&
		!isPushRegistrationSuppressed({ uid: user.uid })
	) {
		return (
			<Alert status="warning" rounded="none" hideIcon>
				<Inline
					gap="4"
					alignItems="center"
					justifyContent="between"
					collapseBelow="sm"
					maxWidth="3xl"
					marginX="auto"
				>
					<Inline gap="3" alignItems="center">
						<Box>
							<Icons.BellAlertSolid size="6" />
						</Box>
						<Stack gap="1">
							<Text fontWeight="semibold">
								Finish enabling notifications on this device
							</Text>
							<Text fontSize="sm" color="muted">
								You've allowed notifications, but this device isn't registered
								yet to receive them.
							</Text>
						</Stack>
					</Inline>
					<ButtonLink
						level="primary"
						size="sm"
						to={generatePath(PREFERENCES_PATH) + SUBSCRIBE_SEARCH}
					>
						Finish setup
					</ButtonLink>
				</Inline>
			</Alert>
		)
	}

	return null
}
